import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Spinner,
  Form,
  Modal,
} from "react-bootstrap";
import {
  FaCalendar,
  FaUser,
  FaEye,
  FaWhatsapp,
  FaFacebook,
  FaTwitter,
  FaLinkedin,
  FaLink,
  FaArrowLeft,
  FaChevronLeft,
  FaChevronRight,
  FaShare,
  FaBookOpen,
  FaClock,
  FaLaptopCode,
  FaDatabase,
  FaGitAlt, 
} from "react-icons/fa";
import { toast } from "react-toastify";
import PublicNavbar from "../../components/public/Navbar";
import Footer from "../../components/public/Footer";
import BlogService from "../../services/blogService";
import "../../styles/blog.css";

const BlogDetails = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentPosts, setRecentPosts] = useState([]);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [currentGalleryIndex, setCurrentGalleryIndex] = useState(0);

  useEffect(() => {
    loadPost();
    loadRecentPosts();
    window.scrollTo(0, 0);
  }, [slug]);

  const loadPost = async () => {
    try {
      setLoading(true);
      const response = await BlogService.getBlogBySlug(slug);
      setPost(response.data);
    } catch (error) {
      console.error("Failed to load post:", error);
      navigate("/blog");
    } finally {
      setLoading(false);
    }
  };

  const loadRecentPosts = async () => {
    try {
      const response = await BlogService.getRecentBlogs(5);
      setRecentPosts(response.data || []);
    } catch (error) {
      console.error("Failed to load recent posts:", error);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  const getCategoryBadge = (category) => {
    const colors = {
      blog: "primary",
      news: "info",
      announcement: "warning",
      event: "success",
    };
    return colors[category] || "secondary";
  };

  const getCategoryIcon = (category) => {
    const icons = {
      blog: "✍️",
      news: "📰",
      announcement: "📢",
      event: "🎉",
    };
    return icons[category] || "📝";
  };

  const calculateReadTime = (content) => {
    const wordsPerMinute = 200;
    const wordCount = (content || "").split(/\s+/).length;
    const readTime = Math.ceil(wordCount / wordsPerMinute);
    return readTime > 0 ? readTime : 1;
  };

  const shareUrls = {
    whatsapp: `https://wa.me/?text=Check out this post: ${post?.title} - ${window.location.href}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${window.location.href}`,
    twitter: `https://twitter.com/intent/tweet?text=${post?.title}&url=${window.location.href}`,
    linkedin: `https://www.linkedin.com/shareArticle?mini=true&url=${window.location.href}&title=${post?.title}`,
  };

  const openGallery = (index) => {
    setCurrentGalleryIndex(index);
    setShowGalleryModal(true);
  };

  const nextImage = () => {
    if (post?.gallery && currentGalleryIndex < post.gallery.length - 1) {
      setCurrentGalleryIndex(currentGalleryIndex + 1);
    }
  };

  const prevImage = () => {
    if (currentGalleryIndex > 0) {
      setCurrentGalleryIndex(currentGalleryIndex - 1);
    }
  };

  if (loading) {
    return (
      <div>
        <PublicNavbar />
        <div className="blog-details-loading text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading amazing content...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!post) return null;

  return (
    <div className="blog-details-wrapper">
      <PublicNavbar />

      {/* Back Button */}
      <div className="blog-details-back">
        <Container>
          <Button
            variant="link"
            className="blog-details-back-btn"
            onClick={() => navigate("/blog")}
          >
            <FaArrowLeft className="me-2" /> Back to Blog
          </Button>
        </Container>
      </div>

      {/* Hero Section */}
      <section className="blog-details-hero">
        <Container>
          <div className="blog-details-hero-content">
            <div className="blog-details-category">
              <span className="blog-details-category-badge">
                {getCategoryIcon(post.category)}{" "}
                {post.category.charAt(0).toUpperCase() + post.category.slice(1)}
              </span>
            </div>
            <h1 className="blog-details-title">{post.title}</h1>
            <div className="blog-details-meta">
              <div className="blog-details-meta-item">
                <FaCalendar /> {formatDate(post.published_date)}
              </div>
              <div className="blog-details-meta-item">
                <FaUser /> {post.author_name || "Admin"}
              </div>
              <div className="blog-details-meta-item">
                <FaEye /> {post.views || 0} views
              </div>
              <div className="blog-details-meta-item">
                <FaClock /> {calculateReadTime(post.content)} min read
              </div>
            </div>
          </div>
        </Container>
        <div className="blog-details-hero-wave">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 120">
            <path
              fill="#ffffff"
              fillOpacity="1"
              d="M0,64L48,80C96,96,192,128,288,128C384,128,480,96,576,85.3C672,75,768,85,864,96C960,107,1056,117,1152,112C1248,107,1344,85,1392,74.7L1440,64L1440,120L1392,120C1344,120,1248,120,1152,120C1056,120,960,120,864,120C768,120,672,120,576,120C480,120,384,120,288,120C192,120,96,120,48,120L0,120Z"
            ></path>
          </svg>
        </div>
      </section>

      {/* Featured Image */}
      {post.featured_image && (
        <div className="blog-details-featured">
          <Container>
            <img
              src={post.featured_image}
              alt={post.title}
              className="blog-details-featured-img"
            />
          </Container>
        </div>
      )}

      {/* Content */}
      <Container className="blog-details-container py-5">
        <Row>
          <Col lg={8} className="mx-auto">
            <div className="blog-details-content">
              <div
                dangerouslySetInnerHTML={{
                  __html: post.content || post.short_description,
                }}
              />
            </div>

            {/* Course Highlights Section - For Full Stack Development Posts */}
            {post.category === "announcement" &&
              post.title.includes("Full Stack") && (
                <div className="blog-details-course-highlights">
                  <h3>🚀 Course Highlights</h3>
                  <Row className="g-3">
                    <Col md={6}>
                      <div className="blog-details-highlight-item">
                        <FaLaptopCode className="me-2" /> Frontend: React.js,
                        HTML, CSS, JavaScript
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="blog-details-highlight-item">
                        <FaDatabase className="me-2" /> Backend: Node.js,
                        Express.js, PHP
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="blog-details-highlight-item">
                        <FaDatabase className="me-2" /> Database: MySQL, MongoDB
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="blog-details-highlight-item">
                        <FaGitAlt className="me-2" /> Version Control: Git &
                        GitHub
                      </div>
                    </Col>
                  </Row>
                </div>
              )}

            {/* Batch Details Section */}
            {post.category === "announcement" && (
              <div className="blog-details-batch-details">
                <h3>📅 Batch Details</h3>
                <Row className="g-3">
                  <Col md={6}>
                    <Card className="blog-details-batch-card">
                      <Card.Body>
                        <div className="blog-details-batch-icon">🎯</div>
                        <h5>Start Date</h5>
                        <p>June 1st, 2024</p>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={6}>
                    <Card className="blog-details-batch-card">
                      <Card.Body>
                        <div className="blog-details-batch-icon">⏰</div>
                        <h5>Duration</h5>
                        <p>6 months (Weekend batches available)</p>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={6}>
                    <Card className="blog-details-batch-card">
                      <Card.Body>
                        <div className="blog-details-batch-icon">🏫</div>
                        <h5>Mode</h5>
                        <p>Classroom (Bhilai) & Online</p>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={6}>
                    <Card className="blog-details-batch-card">
                      <Card.Body>
                        <div className="blog-details-batch-icon">👥</div>
                        <h5>Limited Seats</h5>
                        <p>Only 25 seats per batch</p>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </div>
            )}

            {/* Early Bird Offer Banner */}
            {post.category === "announcement" &&
              post.title.includes("Full Stack") && (
                <div className="blog-details-offer-banner">
                  <div className="blog-details-offer-icon">🎁</div>
                  <div className="blog-details-offer-content">
                    <h4>Early Bird Offer</h4>
                    <p>Enroll before May 15th, 2024 and get:</p>
                    <ul>
                      <li>✨ 20% discount on course fees</li>
                      <li>✨ Free access to additional Python course</li>
                      <li>✨ Free placement preparation workshop</li>
                    </ul>
                    <Button className="blog-details-offer-btn">
                      Register Now →
                    </Button>
                  </div>
                </div>
              )}

            {/* Gallery Section */}
            {post.gallery && post.gallery.length > 0 && (
              <div className="blog-details-gallery mt-5">
                <h4 className="blog-details-gallery-title">
                  <FaBookOpen className="me-2" /> Image Gallery
                </h4>
                <Row className="g-3">
                  {post.gallery.map((img, index) => (
                    <Col md={4} key={img.id}>
                      <div
                        className="blog-details-gallery-item"
                        onClick={() => openGallery(index)}
                      >
                        <img
                          src={img.image_url}
                          alt={img.caption || "Gallery image"}
                        />
                        {img.caption && (
                          <p className="blog-details-gallery-caption">
                            {img.caption}
                          </p>
                        )}
                      </div>
                    </Col>
                  ))}
                </Row>
              </div>
            )}

            {/* Share Buttons */}
            <div className="blog-details-share mt-5">
              <h6>
                <FaShare className="me-2" /> Share this article
              </h6>
              <div className="blog-details-share-buttons">
                <Button
                  variant="success"
                  size="sm"
                  href={shareUrls.whatsapp}
                  target="_blank"
                >
                  <FaWhatsapp /> WhatsApp
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  href={shareUrls.facebook}
                  target="_blank"
                >
                  <FaFacebook /> Facebook
                </Button>
                <Button
                  variant="info"
                  size="sm"
                  href={shareUrls.twitter}
                  target="_blank"
                >
                  <FaTwitter /> Twitter
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  href={shareUrls.linkedin}
                  target="_blank"
                >
                  <FaLinkedin /> LinkedIn
                </Button>
                <Button
                  variant="dark"
                  size="sm"
                  onClick={() => setShowShareModal(true)}
                >
                  <FaLink /> Copy Link
                </Button>
              </div>
            </div>

            {/* Author Box */}
            {post.author_name && (
              <div className="blog-details-author-box">
                <Row className="align-items-center">
                  <Col md={2} className="text-center text-md-start">
                    {post.author_image ? (
                      <img
                        src={post.author_image}
                        alt={post.author_name}
                        className="blog-details-author-img"
                      />
                    ) : (
                      <div className="blog-details-author-placeholder">
                        <FaUser size={40} />
                      </div>
                    )}
                  </Col>
                  <Col md={10}>
                    <h5 className="blog-details-author-name">
                      {post.author_name}
                    </h5>
                    <p className="blog-details-author-bio">
                      {post.author_bio ||
                        "Admission department at Sensible Academy, helping students choose the right career path in technology."}
                    </p>
                  </Col>
                </Row>
              </div>
            )}
          </Col>
        </Row>
      </Container>

      {/* Recent Posts Section */}
      {recentPosts.length > 0 && (
        <div className="blog-details-recent">
          <Container>
            <h3 className="blog-details-recent-title">
              <FaClock className="me-2" /> Recent Articles
            </h3>
            <Row className="g-4">
              {recentPosts
                .filter((p) => p.slug !== slug)
                .slice(0, 3)
                .map((recent) => (
                  <Col md={4} key={recent.id}>
                    <div className="blog-details-recent-card">
                      {recent.featured_image && (
                        <div className="blog-details-recent-img">
                          <img src={recent.featured_image} alt={recent.title} />
                        </div>
                      )}
                      <div className="blog-details-recent-content">
                        <span className="blog-details-recent-category">
                          {getCategoryIcon(recent.category)} {recent.category}
                        </span>
                        <h5>
                          <Link to={`/blog/${recent.slug}`}>
                            {recent.title}
                          </Link>
                        </h5>
                        <small>{formatDate(recent.published_date)}</small>
                      </div>
                    </div>
                  </Col>
                ))}
            </Row>
          </Container>
        </div>
      )}

      {/* Gallery Modal */}
      <Modal
        show={showGalleryModal}
        onHide={() => setShowGalleryModal(false)}
        size="lg"
        centered
        className="blog-details-gallery-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Gallery</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          {post?.gallery && post.gallery[currentGalleryIndex] && (
            <div className="blog-details-gallery-modal-content">
              <img
                src={post.gallery[currentGalleryIndex].image_url}
                alt={
                  post.gallery[currentGalleryIndex].caption || "Gallery image"
                }
                className="w-100"
              />
              {post.gallery[currentGalleryIndex].caption && (
                <p className="blog-details-gallery-modal-caption">
                  {post.gallery[currentGalleryIndex].caption}
                </p>
              )}
              {post.gallery.length > 1 && (
                <>
                  <button
                    className="blog-details-gallery-nav prev"
                    onClick={prevImage}
                  >
                    <FaChevronLeft />
                  </button>
                  <button
                    className="blog-details-gallery-nav next"
                    onClick={nextImage}
                  >
                    <FaChevronRight />
                  </button>
                </>
              )}
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* Share Link Modal */}
      <Modal
        show={showShareModal}
        onHide={() => setShowShareModal(false)}
        centered
        className="blog-details-share-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Copy Link</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="blog-details-share-link-wrapper">
            <Form.Control
              type="text"
              value={window.location.href}
              readOnly
              className="blog-details-share-input"
            />
            <Button
              variant="primary"
              onClick={() => {
                navigator.clipboard.writeText(window.location.href);
                toast.success("Link copied! 🎉");
                setShowShareModal(false);
              }}
            >
              Copy
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      <Footer />
    </div>
  );
};

export default BlogDetails;
